import { LoggerService } from "@/shared/services/LoggerService";

import {
  isValidSidebarTab,
  isValidSidebarViewType,
  SidebarTab,
  SidebarViewType,
} from "./sidebarView";

const WORKSPACE_SIDEBAR_WIDTH_KEY = "WorkspaceSidebar.width_in_pixels";
const WORKSPACE_SIDEBAR_VIEW_KEY = "WorkspaceSidebar.activeView";

/**
 * Per-tab storage (sessionStorage) for workspace sidebar state; survives
 * in-tab navigation but not closing the tab.
 *
 * Stores the sidebar width in pixels and the active view (view type plus,
 * for TabbedSidebar, the selected tab). Only TabbedSidebar and None are
 * persisted; PanelSettings, EventDetails, and CreateEvent are never written,
 * so they do not reopen after a reload. Persisting None replaces a stored
 * `TabbedSidebar:<tab>` value, so closing the sidebar forgets the remembered
 * tab and the next restore falls back to the default tab.
 */
export const WorkspaceSidebarStorage = {
  /**
   * Get the stored sidebar width in pixels.
   * Returns null if no width is stored or if the stored value is invalid.
   */
  getWidth(): number | null {
    try {
      const value = sessionStorage.getItem(WORKSPACE_SIDEBAR_WIDTH_KEY);

      if (value === null) {
        return null;
      }

      const width = Number(value);
      return Number.isFinite(width) ? width : null;
    } catch (e) {
      LoggerService.warn("Failed to get sidebar width", e);
      return null;
    }
  },

  /**
   * Set the sidebar width in pixels.
   */
  setWidth(value: number): void {
    try {
      sessionStorage.setItem(WORKSPACE_SIDEBAR_WIDTH_KEY, value.toString());
    } catch (e) {
      LoggerService.warn("Failed to set sidebar width", e);
    }
  },

  /**
   * Get the stored active view state.
   *
   * Returns an object with:
   * - type: The SidebarViewType
   * - tab: The SidebarTab (only present for TabbedSidebar)
   *
   * Returns null if no view is stored or if the stored value is invalid.
   *
   * Storage format:
   * - "TabbedSidebar:Topics" → { type: TabbedSidebar, tab: Topics }
   * - "TabbedSidebar:Events" → { type: TabbedSidebar, tab: Events }
   * - "None" → { type: None }
   */
  getActiveView(): { type: SidebarViewType; tab?: SidebarTab } | null {
    try {
      const value = sessionStorage.getItem(WORKSPACE_SIDEBAR_VIEW_KEY);

      if (!value) {
        return null;
      }

      // Parse "TabbedSidebar:Tab" format
      if (value.startsWith("TabbedSidebar:")) {
        const [, tab] = value.split(":", 2);
        if (tab && isValidSidebarTab(tab)) {
          return {
            type: SidebarViewType.TabbedSidebar,
            tab,
          };
        }
      }

      // Parse simple view types (e.g., "None")
      if (isValidSidebarViewType(value)) {
        return { type: value };
      }

      return null;
    } catch (e) {
      LoggerService.warn("Failed to get sidebar active view", e);
      return null;
    }
  },

  /**
   * Persist the active view, in the format getActiveView documents. No-op
   * for view types that are not persisted (see the WorkspaceSidebarStorage
   * docstring).
   */
  setActiveView(type: SidebarViewType, tab?: SidebarTab): void {
    if (
      type !== SidebarViewType.TabbedSidebar &&
      type !== SidebarViewType.None
    ) {
      return;
    }

    let value: string;
    if (type === SidebarViewType.TabbedSidebar && tab) {
      value = `${type}:${tab}`;
    } else {
      value = type;
    }

    try {
      sessionStorage.setItem(WORKSPACE_SIDEBAR_VIEW_KEY, value);
    } catch (e) {
      LoggerService.warn("Failed to set sidebar active view", e);
    }
  },
};
